import { createSlice } from '@reduxjs/toolkit';
import {
  fetchTasksThunk,
  fetchTaskThunk,
  createTaskThunk,
  updateTaskThunk,
  updateTaskStatusThunk,
  deleteTaskThunk,
  fetchTaskCommentsThunk,
  addTaskCommentThunk,
} from './tasksActionThunks';

const initialState = {
  items: [],
  loading: { items: false, mutate: false },
  errors: { items: null, mutate: null },
  // Keyed by task id -- kept separate from `items` since comments are only
  // ever needed for one expanded task at a time, not the whole list.
  commentsByTask: {},
};

const upsertTask = (state, task) => {
  const idx = state.items.findIndex((i) => i.id === task.id);
  if (idx !== -1) state.items[idx] = task;
  else state.items.unshift(task);
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // === List ===
      .addCase(fetchTasksThunk.pending, (state) => {
        state.loading.items = true;
        state.errors.items = null;
      })
      .addCase(fetchTasksThunk.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading.items = false;
      })
      .addCase(fetchTasksThunk.rejected, (state, action) => {
        state.loading.items = false;
        state.errors.items = action.payload || 'Failed to load tasks';
      })

      // === Fetch one ===
      .addCase(fetchTaskThunk.fulfilled, (state, action) => {
        upsertTask(state, action.payload);
      })

      // === Create ===
      .addCase(createTaskThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(createTaskThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        state.items.unshift(action.payload);
      })
      .addCase(createTaskThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to create task';
      })

      // === Update ===
      .addCase(updateTaskThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(updateTaskThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        upsertTask(state, action.payload);
      })
      .addCase(updateTaskThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to update task';
      })

      // === Update status (quick inline change) ===
      .addCase(updateTaskStatusThunk.fulfilled, (state, action) => {
        upsertTask(state, action.payload);
      })
      .addCase(updateTaskStatusThunk.rejected, (state, action) => {
        state.errors.mutate = action.payload || 'Failed to update task status';
      })

      // === Delete ===
      .addCase(deleteTaskThunk.pending, (state) => {
        state.loading.mutate = true;
        state.errors.mutate = null;
      })
      .addCase(deleteTaskThunk.fulfilled, (state, action) => {
        state.loading.mutate = false;
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(deleteTaskThunk.rejected, (state, action) => {
        state.loading.mutate = false;
        state.errors.mutate = action.payload || 'Failed to delete task';
      })

      // === Comments ===
      .addCase(fetchTaskCommentsThunk.pending, (state, action) => {
        const taskId = action.meta.arg;
        state.commentsByTask[taskId] = state.commentsByTask[taskId] || { items: [], loading: false, error: null };
        state.commentsByTask[taskId].loading = true;
        state.commentsByTask[taskId].error = null;
      })
      .addCase(fetchTaskCommentsThunk.fulfilled, (state, action) => {
        const { taskId, comments } = action.payload;
        state.commentsByTask[taskId] = { items: comments, loading: false, error: null };
      })
      .addCase(fetchTaskCommentsThunk.rejected, (state, action) => {
        const taskId = action.meta.arg;
        state.commentsByTask[taskId] = state.commentsByTask[taskId] || { items: [], loading: false, error: null };
        state.commentsByTask[taskId].loading = false;
        state.commentsByTask[taskId].error = action.payload || 'Failed to load comments';
      })
      .addCase(addTaskCommentThunk.fulfilled, (state, action) => {
        const { taskId, comment } = action.payload;
        state.commentsByTask[taskId] = state.commentsByTask[taskId] || { items: [], loading: false, error: null };
        state.commentsByTask[taskId].items.push(comment);
        const task = state.items.find((i) => i.id === taskId);
        if (task) task.comment_count = (task.comment_count || 0) + 1;
      });
  },
});

export default tasksSlice.reducer;
